import { CommonModule } from '@angular/common';
import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import {
    FormControl,
    ReactiveFormsModule,
    UntypedFormGroup
} from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslocoModule } from '@jsverse/transloco';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { Observable, combineLatest, of } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { calculateAge } from '../../../core/helpers';
import {
    matchesConditions,
    normalizeValidatorConfigs
} from '../../../core/helpers/dynamic-form-rules';
import {
    PageSectionColumnModel,
    PageSectionFieldValidator
} from '../../../domain/models/dynamic-form-schema.model';
import { Selection } from '../../../domain/models/selection';
import { FullNamePipe } from '../../../features/application/pipes/full-name.pipe';
import { DateMaskDirective } from '../../directives/date-mask.directive';
import { AutocompletePipe } from '../../pipes';
import { MultiSelectionControlComponent } from '../fields/multi-selection-control/multi-selection-control.component';
import { PhoneInputComponent } from '../fields/phone-input/phone-input.component';
import { SingleSelectionControlComponent } from '../fields/single-selection-control/single-selection-control.component';
import { SsnInputComponent } from '../fields/ssn-input/ssn-input.component';

@Component({
    selector: 'app-dynamic-form',
    templateUrl: './dynamic-form.component.html',
    styleUrls: ['./dynamic-form.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CommonModule,
        AutocompletePipe,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatCheckboxModule,
        SingleSelectionControlComponent,
        MultiSelectionControlComponent,
        FlexLayoutModule,
        MatDatepickerModule,
        SsnInputComponent,
        PhoneInputComponent,
        NgxMatSelectSearchModule,
        TranslocoModule,
        FullNamePipe,
        MatCardModule,
        MatDividerModule,
        MatIconModule,
        DateMaskDirective
    ]
})
export class DynamicFormComponent {
    @Input() fields: PageSectionColumnModel[] = [];
    @Input() formGroup!: UntypedFormGroup;
    @Input() formSubmitted = false;
    @Input() sectionValidators: PageSectionFieldValidator[] = [];
    @Input() consentDescribedById?: string;

    private readonly _filterCtrls = new Map<string, FormControl<string>>();
    private readonly _filteredOptions = new Map<
        string,
        Observable<Selection[]>
    >();

    getFilterCtrl(key: string): FormControl<string> {
        if (!this._filterCtrls.has(key)) {
            this._filterCtrls.set(
                key,
                new FormControl<string>('', { nonNullable: true })
            );
        }
        return this._filterCtrls.get(key)!;
    }

    getFilteredOptions(field: PageSectionColumnModel): Observable<Selection[]> {
        if (!this._filteredOptions.has(field.key)) {
            const ctrl = this.getFilterCtrl(field.key);
            const filtered$ = combineLatest([
                field.options$ ?? of([]),
                ctrl.valueChanges.pipe(startWith(''))
            ]).pipe(
                map(([options, filter]) => {
                    if (!filter) return options;
                    const lower = filter.toLowerCase();
                    return options.filter((o) =>
                        o.label.toLowerCase().includes(lower)
                    );
                })
            );
            this._filteredOptions.set(field.key, filtered$);
        }
        return this._filteredOptions.get(field.key)!;
    }

    public currentYear = new Date().getFullYear();

    setAgeAtEnrollment(date: any) {
        const age = calculateAge(date);
        const control =
            this.formGroup.get('ageAtEnrollment') ??
            this.formGroup.get('AgeAtEnrollment');
        if (control) {
            control.setValue(age);
        }
    }

    getValidatorMessage(
        field: PageSectionColumnModel,
        name: string
    ): string | undefined {
        const validators = this.getValidators(field);
        const validator = validators.find((v) => v.name === name);
        return validator?.messageKey ?? validator?.message;
    }

    getValidators(field: PageSectionColumnModel): PageSectionFieldValidator[] {
        const control = this.formGroup.get(field.key);

        if (!control) {
            return [];
        }

        const controlValidators = normalizeValidatorConfigs(
            field.validators
        ).filter(
            (validator) =>
                control.hasError(validator.errorKey ?? validator.name) &&
                control.touched
        );
        const sectionValidators = this.formSubmitted
            ? this.sectionValidators.filter(
                  (validator) =>
                      validator.target === field.key &&
                      this.formGroup.hasError(
                          validator.errorKey ?? validator.name
                      )
              )
            : [];
        return [...controlValidators, ...sectionValidators].filter(
            (validator, index, validators) =>
                validators.findIndex(
                    (candidate) =>
                        (candidate.errorKey ?? candidate.name) ===
                        (validator.errorKey ?? validator.name)
                ) === index
        );
    }

    getMaxLength(field: PageSectionColumnModel): number | null {
        const rawLength = normalizeValidatorConfigs(field.validators).find(
            (v) => v.name === 'maxlength'
        )?.args?.length;
        const length = Number(rawLength);
        return Number.isFinite(length) && length > 0
            ? Math.trunc(length)
            : null;
    }

    isFieldRequired(field: PageSectionColumnModel): boolean {
        const fieldValidators = normalizeValidatorConfigs(field.validators);
        const hasStaticRequired = fieldValidators.some(
            (validator) => validator.name === 'required'
        );
        const hasConditionalFieldRequired = matchesConditions(
            this.formGroup,
            field.requiredWhen
        );
        const hasConditionalSectionRequired = this.sectionValidators.some(
            (validator) =>
                validator.target === field.key &&
                validator.name === 'requireFieldWhen' &&
                matchesConditions(this.formGroup, validator.when)
        );

        return (
            hasStaticRequired ||
            (!!field.requiredWhen && hasConditionalFieldRequired) ||
            hasConditionalSectionRequired
        );
    }

    isFieldVisible(field: PageSectionColumnModel): boolean {
        return matchesConditions(this.formGroup, field.visibleWhen);
    }

    getRequiredErrorKey(field: PageSectionColumnModel): string | undefined {
        return this.sectionValidators.find(
            (validator) =>
                validator.target === field.key &&
                validator.name === 'requireFieldWhen'
        )?.errorKey;
    }

    getAriaDescribedBy(fieldKey: string): string {
        const ids = [
            fieldKey ? fieldKey + '-signature-label' : undefined,
            this.consentDescribedById
        ]
            .filter((id): id is string => !!id)
            .join(' ');
        return ids;
    }
}
